import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tag } from 'lucide-react';
import AboutPage from './AboutPage';
import ModalForm from './ModalForm';

const CatalogPage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesResponse = await axios.get('/api/categories/');
        setCategories(categoriesResponse.data);

        const productsResponse = await axios.get('/api/products/');
        setProducts(productsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category.id === selectedCategory?.id ? null : category);
  };

  const handleProductClick = () => {
    setShowModal(true);
  };

  const filteredProducts = selectedCategory
    ? products.filter(product => product.category?.id === selectedCategory.id)
    : products;

  if (categories.length === 0 || products.length === 0) {
    return <div className="container mx-auto px-4 py-8">Загрузка данных...</div>;
  }

  return (
    <div className="w-full bg-gray-50">
      <div className="max-w-screen-xl mx-auto px-4 py-10">
        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-8 text-gray-800">Каталог товаров</h1>

        {/* Категории */}
        <div className="flex overflow-x-auto pb-4 mb-6 space-x-3 scrollbar-hide">
          {categories.map(category => {
            const isSelected = selectedCategory?.id === category.id;
            return (
              <div
                key={category.id}
                onClick={() => handleCategoryClick(category)}
                className={`flex-shrink-0 w-28 h-24 rounded-lg shadow-sm overflow-hidden cursor-pointer transition-all duration-200 ${
                  isSelected
                    ? 'ring-2 ring-blue-500 bg-blue-100 scale-105'
                    : 'bg-white hover:scale-105'
                }`}
              >
                {category.image && (
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-14 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <div className="p-1 text-center">
                  <h3 className={`text-xs font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                    {category.name}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>

        {/* Товары */}
        <div className="w-full">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
            {filteredProducts.map(product => (
              <div
                key={product.id}
                className="relative bg-white rounded-lg shadow hover:shadow-md transition duration-300 cursor-pointer"
                onClick={handleProductClick}
              >
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">{product.name}</h3>
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">Размер:</span> {product.size}
                  </p>

                  {/* Блок цены */}
                  <div className="mt-1 space-y-1">
                    {/* Вариант 1: текущая и старая цена */}
                    {product.currentPrice && (
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-red-600">{product.currentPrice} ₽</span>
                        {product.oldPrice && (
                          <>
                            <span className="text-xs text-gray-400 line-through">{product.oldPrice} ₽</span>
                            <Tag size={14} className="text-red-600 ml-1" />
                          </>
                        )}
                      </div>
                    )}

                    {/* Вариант 2: цена за м.п. и/или т */}
                    {!product.currentPrice && (product.price_per_meter || product.price_per_ton) && (
                      <div className="space-y-1 text-sm text-gray-800">
                        {product.price_per_meter && (
                          <div className="flex items-center space-x-1">
                            <span>Цена за м.п:</span>
                            <span className="text-red-600 font-bold">{product.price_per_meter} ₽</span>
                          </div>
                        )}
                        {product.price_per_ton && (
                          <div className="flex items-center space-x-1">
                            <span>Цена за т:</span>
                            <span className="text-red-600 font-bold">{product.price_per_ton} ₽</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Модальное окно */}
        {showModal && (
          <ModalForm
            onClose={() => {
              setShowModal(false);
            }}
          />
        )}

        {/* О нас */}
        <div className="mt-12">
          <AboutPage />
        </div>
      </div>
    </div>
  );
};

export default CatalogPage;
